import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import AppLayout from "../layouts/AppLayout.vue";
import { useAuth } from "@/auth/composables/useAuth";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: { name: "404" },
  },
  {
    name: "admin",
    path: "/",
    component: AppLayout,
    redirect: { name: "dashboard" },
    children: [
      {
        name: "dashboard",
        path: "dashboard",
        component: () => import("../pages/dashboard/Dashboard.vue"),
      },
    ],
  },
  {
    name: "passes",
    path: "/passes",
    component: AppLayout,
    redirect: { name: "single-passes" },
    children: [
      {
        name: "single-passes",
        path: "",
        component: () => import("../pages/single-passes/PassesOverview.vue"),
      },
      {
        name: "single-pass-detail",
        path: ":id",
        component: () => import("../pages/single-passes/PassDetails.vue"),
        props: true,
      },
    ],
  },
  {
    name: "users",
    path: "/users",
    component: AppLayout,
    redirect: { name: "search" },
    children: [
      {
        name: "search",
        path: "",
        component: () => import("../pages/users/UserSearch.vue"),
      },
      {
        name: "user-detail",
        path: ":id",
        component: () => import("../pages/users/UserDetails.vue"),
        props: true,
      },
    ],
  },
  {
    name: "bundles",
    path: "/bundles",
    component: AppLayout,
    redirect: { name: "bundle-overview" },
    children: [
      {
        name: "bundle-overview",
        path: "",
        component: () => import("../pages/bundles/BundlesOverview.vue"),
      },
      {
        name: "configure",
        path: "configure",
        component: () => import("../pages/bundles/CreateBundle.vue"),
      },
      {
        name: "bundle-details",
        path: ":id",
        component: () => import("../pages/bundles/BundleDetails.vue"),
        props: true,
      },
      {
        name: "batch-details",
        path: "batch/:id",
        component: () => import("../pages/bundles/BatchDetails.vue"),
        props: true,
      },
      {
        name: "key-details",
        path: "key/:id",
        component: () => import("../pages/bundles/KeyDetails.vue"),
        props: true,
      },
    ],
  },
  {
    name: "magic-links",
    path: "/magic-links",
    component: AppLayout,
    redirect: { name: "links" },
    children: [
      {
        name: "links",
        path: "links",
        component: () => import("../pages/magic-links/MagicLinksOverview.vue"),
      },
      {
        name: "magic-link-detail",
        path: ":id",
        component: () => import("../pages/magic-links/MagicLinkDetails.vue"),
        props: true,
      },
      {
        name: "generate",
        path: "generate",
        component: () => import("../pages/magic-links/CreateMagicLinks.vue"),
      },
    ],
  },
  {
    name: "product-qr-bundles",
    path: "/product-qr-bundles",
    component: AppLayout,
    redirect: { name: "product-qr-bundles-overview" },
    children: [
      {
        name: "product-qr-bundles-overview",
        path: "",
        component: () => import("../pages/product-qr/ProductQrBundlesOverview.vue"),
      },
      {
        name: "product-qr-bundle-details",
        path: ":id",
        component: () => import("../pages/product-qr/ProductQrBundleDetails.vue"),
        props: true,
      },
      {
        name: "product-qr-bundle-configure",
        path: "configure",
        component: () => import("../pages/product-qr/CreateProductQrBundle.vue"),
      },
      {
        name: "product-qr-bundle-batch",
        path: "batch/generate/:id",
        component: () => import("../pages/product-qr/CreateProductQr.vue"),
        props: true,
      },
      {
        name: "product-qr-batch-details",
        path: "batch/:id",
        component: () => import("../pages/product-qr/ProductQrBatchDetails.vue"),
        props: true,
      },
    ],
  },
  {
    name: "logs",
    path: "/logs",
    component: AppLayout,
    redirect: { name: "log" },
    children: [
      {
        name: "log",
        path: "",
        component: () => import("../pages/logs/LogsOverview.vue"),
      },
    ],
  },
  {
    name: "404",
    path: "/404",
    component: () => import("../pages/404.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    } else {
      window.scrollTo(0, 0);
    }
  },
  routes,
});

router.beforeEach(async (to, from, next) => {
  const { login, fetchCurrentUser, isAuthenticated } = useAuth();

  try {
    await fetchCurrentUser();
  } catch (error) {
    console.log("Initialization error", error);
  }

  if (!isAuthenticated.value) {
    try {
      await login();
      next();
    } catch (error) {
      next("/"); // Redirect to home if login fails
    }
  } else {
    next();
  }
});

export default router;
